import { z } from 'zod'
import { useZorm } from 'react-zorm'
import { FormField, fieldDescription } from '@codecorp/zorm-conventions'
import { useState } from 'react'
import { ShowBarcode } from '../components/show-barcode'
import { crccs, processCrccsFile } from '@codecorp/barcode-data-generator'
import { useDropzone } from 'react-dropzone'
import { Button } from '@codecorp/components'
import { useFormReset } from '../hooks/use-form'
import { useShowModal } from '../hooks/use-show-modal'

const FormSchema = z.object({
    label: z.string().describe(
        fieldDescription({
            placeHolder: 'This name will be placed under the QR Code.',
        })
    ),
    contents: z
        .string()
        .min(1)
        .describe(
            fieldDescription({
                label: 'Data to Encode',
                placeHolder: 'Enter CRCCS Code.',
            })
        ),
})

export const CrccsBarcodeCreator = () => {
    const { setSubmittedBarcode, handleFormReset } = useFormReset(undefined)

    const [fileName, setFileName] = useState<string | null>(null)
    const [fileError, setFileError] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [inputValue, setInputValue] = useState<string>('')
    const showModal = useShowModal()

    // Define the regex pattern to match only letters, numbers, spaces, and semicolons
    const zo = useZorm('crccsBarcode', FormSchema, {
        onValidSubmit: async ({ preventDefault, data }) => {
            preventDefault()

            setSubmittedBarcode(data)
            showModal(
                <ShowBarcode
                    label={data.label}
                    barcode={crccs(
                        data.contents.split(';').map((x: string) => x.trim())
                    )}
                />
            )
        },
    })

    const onDrop = (acceptedFiles: File[]) => {
        const file = acceptedFiles[0]
        if (!file) {
            setFileError('File type not accepted. Please upload a .crccs file.')
            return
        }
        const reader = new FileReader()

        reader.onload = () => {
            const content = reader.result as string
            const result = processCrccsFile(content)
            setErrorMessage(null)
            setFileError(null)
            setInputValue(result)
            setFileName(file.name) // Set the file name (optional, for display purposes)
        }

        if (file) {
            reader.readAsText(file)
        }
    }

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: {
            'text/plain': ['.crccs'],
        },
    })
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value) // Update state if the value is valid
        setErrorMessage(null) // Clear any previous error message
    }
    const handleClear = () => {
        setInputValue('') // Clear the text input
        setFileName(null) // Clear the file name
        setErrorMessage(null) // Clear the error message
        setFileError(null) //Clear the file error
    }

    const clearFile = () => {
        setFileName(null) // Clear the file name state
    }

    return (
        <div>
            <form
                ref={zo.ref}
                className="ml-10 mr-10 leading-10"
                onReset={handleFormReset}
            >
                {zo.fields.label(FormField)}
                <label>Upload CRCCS File</label>
                <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center ${
                        isDragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300'
                    }`}
                >
                    <input {...getInputProps()} />
                    <p>
                        {fileName
                            ? `File selected: ${fileName}`
                            : "Drag 'n' drop a CRCCS file here, or click the Import a File button to upload file."}
                        <br />
                        <Button
                            filled
                            onPress={open}
                        >
                            Import a File
                        </Button>
                    </p>
                </div>
                {fileError && (
                    <p className="mt-2 text-sm text-red-600">{fileError}</p>
                )}
                {fileName && (
                    <div className="mt-2">
                        <button
                            type="button"
                            className="rounded-md bg-red-700 px-4 py-2 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-700"
                            onClick={clearFile}
                        >
                            Clear
                        </button>
                    </div>
                )}
                <label>Data to Encode</label>
                <input
                    type="text"
                    name="contents"
                    value={inputValue}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${
                        errorMessage ? 'border-red-600' : 'border-gray-300'
                    } p-2`}
                    placeholder="Enter CRCCS Code"
                />
                {errorMessage && (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                )}
                {/* {zo.fields.contents(FormField)} */}
                <Button
                    type="reset"
                    filled
                    secondary
                    className="mr-5 mt-5"
                    onPress={handleClear}
                >
                    Clear
                </Button>
                <Button
                    type="submit"
                    filled
                    className="hover:bg-blue-800"
                >
                    Generate
                </Button>
            </form>
        </div>
    )
}
